import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import LenderLogo from '../components/lender_packs/LenderLogo';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, FileText, Users, ClipboardList, Search, RefreshCw, Trash2,
  Landmark, Loader2, ChevronRight, Send, AlertCircle, ArrowUpDown, LayoutList,
  ChevronDown, ChevronUp, ExternalLink, User, Building2, HardHat, Sparkles,
  Settings as SettingsIcon, Home, LayoutDashboard, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CreatePackDialog from '../components/lender_packs/CreatePackDialog';
import PackBuilder from '../components/lender_packs/PackBuilder';
import PackDashboardSummary from '../components/lender_packs/PackDashboardSummary';
import MyDashboardTab from '../components/lender_packs/MyDashboardTab';
import JourneyTab from '../components/lender_packs/JourneyTab';
import BetaBadge from '../components/BetaBadge';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  waiting_on_info: 'Waiting on Info',
  ready_for_review: 'Ready for Review',
  changes_required: 'Changes Required',
  ready_to_submit: 'Ready to Submit',
  lodged: 'Lodged with Lender',
  mir_outstanding: 'MIR Outstanding',
  waiting_on_client: 'Waiting on Client',
  waiting_on_derwent: 'Waiting on Derwent',
  waiting_on_third_party: 'Waiting on Third Party',
  all_mirs_satisfied: 'All MIRs Satisfied · Waiting on Lender',
  back_with_lender: 'Back with Lender',
  conditional_approval: 'Conditional Approval',
  formal_approval: 'Formal Approval',
  completed: 'Completed',
};

const STATUS_TONES = {
  not_started: 'bg-slate-100 text-slate-700 border-slate-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  waiting_on_info: 'bg-amber-100 text-amber-800 border-amber-200',
  ready_for_review: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  changes_required: 'bg-rose-100 text-rose-800 border-rose-200',
  ready_to_submit: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  lodged: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  mir_outstanding: 'bg-amber-100 text-amber-800 border-amber-200',
  waiting_on_client: 'bg-amber-100 text-amber-800 border-amber-200',
  waiting_on_derwent: 'bg-blue-100 text-blue-800 border-blue-200',
  waiting_on_third_party: 'bg-purple-100 text-purple-800 border-purple-200',
  all_mirs_satisfied: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  back_with_lender: 'bg-teal-100 text-teal-800 border-teal-200',
  conditional_approval: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  formal_approval: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  completed: 'bg-slate-200 text-slate-800 border-slate-300',
};

// Urgency score used for the "urgency" sort mode. Higher = more urgent.
const urgencyScore = (p) => {
  let score = 0;
  if (p.sla_indicator === 'overdue') score += 100 + Math.abs(p.days_remaining || 0);
  else if (p.sla_indicator === 'due_today') score += 90;
  else if (p.days_remaining !== null && p.days_remaining !== undefined && p.days_remaining <= 2) score += 60;
  if (p.status === 'changes_required') score += 80;
  if (p.status === 'waiting_on_derwent') score += 40;
  if ((p.mir_counts?.outstanding || 0) > 0) score += 30 + (p.mir_counts.outstanding * 5);
  if (p.status === 'ready_to_submit' || p.completion === 100) score += 45;
  if (p.status === 'waiting_on_info') score += 25;
  // freshness — inactive files bubble up
  const daysIdle = p.last_updated_at ? Math.floor((Date.now() - new Date(p.last_updated_at)) / 86400000) : 0;
  if (daysIdle >= 3) score += Math.min(20, daysIdle);
  return score;
};

// Who owns the next action right now?
const nextActionOwner = (p) => {
  switch (p.status) {
    case 'not_started':
    case 'in_progress':
    case 'ready_to_submit':
      return { label: p.broker || 'Broker', tone: 'blue', icon: User };
    case 'waiting_on_info':
      return { label: p.broker || 'Broker', tone: 'amber', icon: User };
    case 'ready_for_review':
    case 'changes_required':
      return { label: p.credit_team_member || 'Credit', tone: 'indigo', icon: HardHat };
    case 'lodged':
    case 'back_with_lender':
    case 'all_mirs_satisfied':
    case 'conditional_approval':
    case 'formal_approval':
      return { label: p.lender || 'Lender', tone: 'teal', icon: Landmark };
    case 'waiting_on_client':
      return { label: p.client_name || 'Client', tone: 'amber', icon: User };
    case 'waiting_on_derwent':
      return { label: 'Derwent', tone: 'blue', icon: HardHat };
    case 'waiting_on_third_party':
      return { label: '3rd party', tone: 'purple', icon: Building2 };
    case 'mir_outstanding':
      return { label: p.credit_team_member || p.broker || 'Broker', tone: 'amber', icon: HardHat };
    default:
      return { label: '—', tone: 'slate', icon: User };
  }
};

const OWNER_TONE_CLASS = {
  blue:   'bg-blue-50 text-blue-800 border-blue-200',
  amber:  'bg-amber-50 text-amber-800 border-amber-200',
  indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  teal:   'bg-teal-50 text-teal-800 border-teal-200',
  purple: 'bg-purple-50 text-purple-800 border-purple-200',
  slate:  'bg-slate-50 text-slate-700 border-slate-200',
};

const SORT_OPTIONS = [
  { value: 'urgency',       label: 'Urgency' },
  { value: 'last_updated',  label: 'Last updated' },
  { value: 'expected_asc',  label: 'Expected response · earliest' },
  { value: 'expected_desc', label: 'Expected response · latest' },
  { value: 'client',        label: 'Client name (A–Z)' },
  { value: 'lender',        label: 'Lender (A–Z)' },
  { value: 'broker',        label: 'Broker (A–Z)' },
  { value: 'credit',        label: 'Credit team (A–Z)' },
  { value: 'completion',    label: 'Completion %' },
  { value: 'mirs',          label: 'Open MIRs' },
];

const GROUP_OPTIONS = [
  { value: 'none',   label: 'No grouping' },
  { value: 'status', label: 'Group by status' },
  { value: 'lender', label: 'Group by lender' },
  { value: 'broker', label: 'Group by broker' },
  { value: 'credit', label: 'Group by credit team' },
  { value: 'sla',    label: 'Group by SLA state' },
];

export default function LenderSubmissionPacks() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [packs, setPacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openedPackId, setOpenedPackId] = useState(null);

  // Deep-link: if we arrive with ?open=<packId>, open that pack automatically.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const id = sp.get('open');
    if (id) {
      setOpenedPackId(id);
      // For post-lodgement packs the "Post-Lodgement" tab is more relevant;
      // it's cheap to try both — we simply prefer the current tab and let the
      // pack drawer resolve from any list.
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [filters, setFilters] = useState({
    q: '', status: '', broker: '', lender: '', credit: '',
    overdue: false, outstanding_mirs: false,
  });

  const [sortBy, setSortBy] = useState('urgency');
  const [groupBy, setGroupBy] = useState('none');
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const auth = useMemo(() => {
    const h = { Authorization: `Bearer ${token}` };
    if (localStorage.getItem('mcc-demo-mode') === 'on') h['X-Demo-Mode'] = 'on';
    return h;
  }, [token]);

  const loadPacks = useCallback(async (tab) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (tab === 'active') params.set('phase', 'pre_lodgement');
    else if (tab === 'post_lodgement') params.set('phase', 'post_lodgement');
    else if (tab === 'completed') params.set('phase', 'completed');
    if (filters.status && tab !== 'completed') params.set('status', filters.status);
    if (filters.broker) params.set('broker', filters.broker);
    if (filters.lender) params.set('lender', filters.lender);
    if (filters.q) params.set('q', filters.q);
    if (filters.overdue) params.set('overdue', 'true');
    if (filters.outstanding_mirs) params.set('outstanding_mirs', 'true');
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs?${params}`, { headers: auth });
      const d = await res.json();
      setPacks(d.packs || []);
    } catch (e) {
      toast.error(`Failed to load packs — ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [auth, filters]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/templates`, { headers: auth });
      const d = await res.json();
      setTemplates(d.templates || []);
    } catch { /* silent */ }
  }, [auth]);

  useEffect(() => {
    if (openedPackId) return;
    if (activeTab === 'templates') loadTemplates();
    else loadPacks(activeTab);
  }, [activeTab, openedPackId, loadPacks, loadTemplates]);

  const deletePack = async (packId) => {
    if (!confirm('Delete this pack? This cannot be undone.')) return;
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}`, {
        method: 'DELETE', headers: auth,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Pack deleted');
      loadPacks(activeTab);
    } catch (e) { toast.error(`Delete failed — ${e.message}`); }
  };

  // Client-side credit filter (server does not support it directly yet)
  const filteredPacks = useMemo(() => {
    if (!filters.credit) return packs;
    return packs.filter(p => (p.credit_team_member || '') === filters.credit);
  }, [packs, filters.credit]);

  // Sort
  const sortedPacks = useMemo(() => {
    const list = [...filteredPacks];
    const t = (s) => (s || '').toLowerCase();
    switch (sortBy) {
      case 'urgency':       list.sort((a, b) => urgencyScore(b) - urgencyScore(a) || t(a.client_name).localeCompare(t(b.client_name))); break;
      case 'last_updated':  list.sort((a, b) => new Date(b.last_updated_at || 0) - new Date(a.last_updated_at || 0)); break;
      case 'expected_asc':  list.sort((a, b) => new Date(a.expected_response_date || '9999-01-01') - new Date(b.expected_response_date || '9999-01-01')); break;
      case 'expected_desc': list.sort((a, b) => new Date(b.expected_response_date || '1900-01-01') - new Date(a.expected_response_date || '1900-01-01')); break;
      case 'client':        list.sort((a, b) => t(a.client_name).localeCompare(t(b.client_name))); break;
      case 'lender':        list.sort((a, b) => t(a.lender).localeCompare(t(b.lender)) || t(a.client_name).localeCompare(t(b.client_name))); break;
      case 'broker':        list.sort((a, b) => t(a.broker).localeCompare(t(b.broker)) || t(a.client_name).localeCompare(t(b.client_name))); break;
      case 'credit':        list.sort((a, b) => t(a.credit_team_member).localeCompare(t(b.credit_team_member)) || t(a.client_name).localeCompare(t(b.client_name))); break;
      case 'completion':    list.sort((a, b) => (b.completion || 0) - (a.completion || 0)); break;
      case 'mirs':          list.sort((a, b) => (b.mir_counts?.outstanding || 0) - (a.mir_counts?.outstanding || 0)); break;
      default: break;
    }
    return list;
  }, [filteredPacks, sortBy]);

  // Group
  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map();
    sortedPacks.forEach(p => {
      let key;
      switch (groupBy) {
        case 'status': key = STATUS_LABELS[p.status] || 'Unknown'; break;
        case 'lender': key = p.lender || 'No lender'; break;
        case 'broker': key = p.broker || 'No broker'; break;
        case 'credit': key = p.credit_team_member || 'No credit team'; break;
        case 'sla':
          if (p.sla_indicator === 'overdue')     key = '🔴 Overdue';
          else if (p.sla_indicator === 'due_today') key = '🟠 Due today';
          else if (p.sla_indicator === 'on_track')  key = '🟢 On track';
          else if (p.sla_indicator === 'waiting_on_client') key = '🟡 Waiting on client';
          else if (p.sla_indicator === 'waiting_on_derwent') key = '🔵 Waiting on Derwent';
          else if (p.sla_indicator === 'waiting_on_third_party') key = '🟣 Waiting on 3rd party';
          else if (p.sla_indicator === 'back_with_lender') key = '🟦 Back with lender';
          else key = '⚪ No SLA yet';
          break;
        default: key = 'All';
      }
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    return [...map.entries()].map(([label, items]) => ({ label, items }));
  }, [sortedPacks, groupBy]);

  const toggleGroup = (label) => setCollapsedGroups(g => ({ ...g, [label]: !g[label] }));

  // ── Pack builder view ─────────────────────────────────────
  if (openedPackId) {
    return (
      <PackBuilder
        packId={openedPackId}
        onBack={() => { setOpenedPackId(null); }}
        onPackChange={() => loadPacks(activeTab)}
      />
    );
  }

  const uniqueBrokers = [...new Set(packs.map(p => p.broker).filter(Boolean))].sort();
  const uniqueLenders = [...new Set(packs.map(p => p.lender).filter(Boolean))].sort();
  const uniqueCredit  = [...new Set(packs.map(p => p.credit_team_member).filter(Boolean))].sort();

  const activeFilterCount = [
    filters.q, filters.status, filters.broker, filters.lender, filters.credit,
    filters.overdue && 'overdue', filters.outstanding_mirs && 'mirs',
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lender-packs-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Home link */}
        <div className="mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-900 hover:text-indigo-700 transition select-none"
            title="Back to Derwent AI home"
            data-testid="lp-home-logo"
          >
            <Home className="w-4 h-4" />
            <span className="text-sm font-black tracking-tight">DERWENT<span className="text-indigo-600">AI</span></span>
          </Link>
        </div>
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-blue-600" />
              My Derwent Hub
              <BetaBadge size="sm" />
              {localStorage.getItem('mcc-demo-mode') === 'on' && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800"
                  data-testid="lp-demo-badge"
                >
                  <Sparkles className="w-3 h-3" /> Demo Mode
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Live overview of every active and lodged file. Priority-ranked actions surface automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-pack-btn">
              <Plus className="w-4 h-4 mr-1" /> New pack
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-7 w-fit mb-4">
            <TabsTrigger value="my_dashboard" data-testid="tab-my-dashboard" className="text-xs font-bold gap-1.5">
              <LayoutDashboard className="w-3 h-3" /> My Dashboard
            </TabsTrigger>
            <TabsTrigger value="journey" data-testid="tab-journey" className="text-xs font-bold gap-1.5">
              <GitBranch className="w-3 h-3" /> Journey
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active" className="text-xs font-bold gap-1.5">
              <FileText className="w-3 h-3" /> Pre-Lodgement
            </TabsTrigger>
            <TabsTrigger value="post_lodgement" data-testid="tab-post-lodgement" className="text-xs font-bold gap-1.5">
              <Send className="w-3 h-3" /> Post-Lodgement
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed" className="text-xs font-bold gap-1.5">
              <Users className="w-3 h-3" /> Completed
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="text-xs font-bold gap-1.5">
              <Landmark className="w-3 h-3" /> Templates
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="text-xs font-bold gap-1.5">
              <SettingsIcon className="w-3 h-3" /> Settings
            </TabsTrigger>
          </TabsList>

          {activeTab === 'my_dashboard' && (
            <MyDashboardTab onOpenPack={(id) => setOpenedPackId(id)} />
          )}

          {activeTab === 'journey' && (
            <JourneyTab onOpenPack={(id) => setOpenedPackId(id)} />
          )}

          {activeTab === 'settings' && (
            <LenderPacksSettings backendUrl={backendUrl} />
          )}

          {['active', 'post_lodgement', 'completed'].includes(activeTab) && (
            <>
              {/* Overview cards + urgent-action rail */}
              {activeTab !== 'completed' && (
                <PackDashboardSummary
                  tab={activeTab}
                  packs={packs}
                  currentBroker={user?.name}
                  onOpenPack={(id) => setOpenedPackId(id)}
                  onFilterOverdue={() => setFilters(f => ({ ...f, overdue: !f.overdue }))}
                  onFilterMirs={() => setFilters(f => ({ ...f, outstanding_mirs: !f.outstanding_mirs }))}
                  onFilterStatus={(status) => setFilters(f => ({ ...f, status }))}
                />
              )}

              {/* Filters + sort + group */}
              <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
                      placeholder="Search client / app # / deal #" className="pl-8 h-9 text-sm" data-testid="pack-search" />
                  </div>
                  {activeTab === 'active' && (
                    <Select value={filters.status || 'all'} onValueChange={v => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                      <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'completed').map(([k, l]) => (
                          <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={filters.broker || 'all'} onValueChange={v => setFilters({ ...filters, broker: v === 'all' ? '' : v })}>
                    <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="filter-broker"><SelectValue placeholder="Broker" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All brokers</SelectItem>
                      {uniqueBrokers.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filters.lender || 'all'} onValueChange={v => setFilters({ ...filters, lender: v === 'all' ? '' : v })}>
                    <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="filter-lender"><SelectValue placeholder="Lender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All lenders</SelectItem>
                      {uniqueLenders.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filters.credit || 'all'} onValueChange={v => setFilters({ ...filters, credit: v === 'all' ? '' : v })}>
                    <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="filter-credit"><SelectValue placeholder="Credit team" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All credit</SelectItem>
                      {uniqueCredit.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {activeTab === 'post_lodgement' && (
                    <>
                      <button
                        onClick={() => setFilters({ ...filters, overdue: !filters.overdue })}
                        className={`h-9 px-3 rounded-md text-xs font-bold flex items-center gap-1 border ${filters.overdue ? 'bg-rose-100 border-rose-300 text-rose-800' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                        data-testid="filter-overdue"
                      >
                        <AlertCircle className="w-3 h-3" /> Overdue
                      </button>
                      <button
                        onClick={() => setFilters({ ...filters, outstanding_mirs: !filters.outstanding_mirs })}
                        className={`h-9 px-3 rounded-md text-xs font-bold border ${filters.outstanding_mirs ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                        data-testid="filter-mirs"
                      >
                        MIRs open
                      </button>
                    </>
                  )}
                  <div className="flex items-center gap-2 ml-auto">
                    <div className="flex items-center gap-1 border rounded-md pl-1.5 h-9" style={{ borderColor: '#e2e8f0' }}>
                      <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" />
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-[190px] h-8 text-xs border-0 bg-transparent" data-testid="sort-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1 border rounded-md pl-1.5 h-9" style={{ borderColor: '#e2e8f0' }}>
                      <LayoutList className="w-3.5 h-3.5 text-slate-500" />
                      <Select value={groupBy} onValueChange={setGroupBy}>
                        <SelectTrigger className="w-[170px] h-8 text-xs border-0 bg-transparent" data-testid="group-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {GROUP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <button onClick={() => loadPacks(activeTab)} className="p-2 rounded-md hover:bg-slate-100" title="Refresh" data-testid="pack-refresh">
                      <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                {activeFilterCount > 0 && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Active filters</span>
                    <span className="text-[11px] font-bold text-slate-700">{sortedPacks.length} match{sortedPacks.length === 1 ? '' : 'es'}</span>
                    <button
                      onClick={() => setFilters({ q: '', status: '', broker: '', lender: '', credit: '', overdue: false, outstanding_mirs: false })}
                      className="text-[11px] font-bold text-blue-600 hover:text-blue-800 underline"
                      data-testid="clear-filters"
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>

              {/* Table / grouped view */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {loading ? (
                  <div className="text-center py-16 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading packs…
                  </div>
                ) : sortedPacks.length === 0 ? (
                  <div className="text-center py-16 text-slate-500 italic">
                    {activeTab === 'active' ? 'No active packs. Click "New pack" to start one.'
                      : activeTab === 'post_lodgement' ? 'No lodged applications yet.'
                      : 'No completed packs yet.'}
                  </div>
                ) : groups ? (
                  <div className="divide-y divide-slate-200">
                    {groups.map(g => {
                      const collapsed = collapsedGroups[g.label];
                      return (
                        <div key={g.label} data-testid={`group-${g.label}`}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(g.label)}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left border-b border-slate-200"
                          >
                            <div className="flex items-center gap-2">
                              {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                              <span className="text-[12px] font-black text-slate-900">{g.label}</span>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 tabular-nums">{g.items.length}</span>
                            </div>
                          </button>
                          {!collapsed && <PackTable packs={g.items} isPost={activeTab === 'post_lodgement'} onOpen={setOpenedPackId} onDelete={deletePack} />}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <PackTable packs={sortedPacks} isPost={activeTab === 'post_lodgement'} onOpen={setOpenedPackId} onDelete={deletePack} />
                )}
              </div>
            </>
          )}

          {/* ── Templates tab ── */}
          {activeTab === 'templates' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(t => (
                <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <LenderLogo lender={t.lender} size="md" />
                    <h3 className="text-base font-black text-slate-900">{t.lender}</h3>
                  </div>
                  <div className="text-xs text-slate-500">Version {t.version || '—'}</div>
                  <div className="text-xs text-slate-600 mt-3">{t.section_count} sections</div>
                  <Button variant="outline" size="sm" className="mt-4 w-full text-xs" onClick={() => { setActiveTab('active'); setShowCreate(true); }}>
                    Start a pack with {t.lender}
                  </Button>
                </div>
              ))}
              {templates.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500 italic">No templates yet.</div>
              )}
            </div>
          )}
        </Tabs>
      </div>

      <CreatePackDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(pack) => { loadPacks('active'); setOpenedPackId(pack.id); }}
      />
    </div>
  );
}

// ─── The enhanced pack row table ────────────────────────────────
function PackTable({ packs, isPost, onOpen, onDelete }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-500">
            <th className="text-left px-3 py-2">Client / App</th>
            <th className="text-left px-3 py-2">Lender · Broker</th>
            <th className="text-left px-3 py-2">Credit team</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Next action</th>
            {isPost ? (
              <>
                <th className="text-left px-3 py-2">Lodged / Expected</th>
                <th className="text-right px-3 py-2">SLA</th>
                <th className="text-right px-3 py-2">MIRs</th>
              </>
            ) : (
              <th className="text-right px-3 py-2">Completion</th>
            )}
            <th className="text-left px-3 py-2">Last updated</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {packs.map(p => {
            const owner = nextActionOwner(p);
            const OwnerIcon = owner.icon;
            return (
              <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer group"
                onClick={() => onOpen(p.id)} data-testid={`pack-row-${p.id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-black text-slate-900 text-[12.5px]">{p.client_name}</div>
                  <div className="text-[10.5px] text-slate-500 tabular-nums mt-0.5">
                    {p.application_number ? <span>App #{p.application_number}</span> : <span className="italic">No app #</span>}
                    {p.deal_number ? <span> · Deal #{p.deal_number}</span> : ''}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <LenderLogo lender={p.lender} size="sm" />
                    <div>
                      <div className="text-slate-800 font-bold text-[11.5px]">{p.lender}</div>
                      <div className="text-[10.5px] text-slate-500 mt-0.5">{p.broker || '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-600 text-[11px]">{p.credit_team_member || '—'}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9.5px] font-black whitespace-nowrap border ${STATUS_TONES[p.status]}`}>
                    {STATUS_LABELS[p.status]}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${OWNER_TONE_CLASS[owner.tone]}`}>
                    <OwnerIcon className="w-2.5 h-2.5" />
                    {owner.label}
                  </span>
                </td>
                {isPost ? (
                  <>
                    <td className="px-3 py-2.5">
                      <div className="text-slate-700 tabular-nums text-[11px]">
                        {p.lodged_at ? new Date(p.lodged_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}
                      </div>
                      <div className="text-slate-500 tabular-nums text-[10.5px] mt-0.5">
                        Due {p.expected_response_date ? new Date(p.expected_response_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {p.days_remaining === null || p.days_remaining === undefined ? (
                        <span className="text-slate-400 text-[10px]">—</span>
                      ) : (
                        <SlaCountdown days={p.days_remaining} indicator={p.sla_indicator} sla={p.sla_days} />
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1 tabular-nums font-bold text-[11px]">
                        <span className={(p.mir_counts?.outstanding || 0) > 0 ? 'text-amber-700' : 'text-slate-400'}>
                          {p.mir_counts?.outstanding || 0}
                        </span>
                        <span className="text-slate-400">/</span>
                        <span className="text-slate-500">{p.mir_counts?.total || 0}</span>
                      </span>
                    </td>
                  </>
                ) : (
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${p.completion === 100 ? 'bg-emerald-500' : p.completion >= 75 ? 'bg-blue-500' : p.completion >= 40 ? 'bg-indigo-500' : 'bg-slate-400'}`} style={{ width: `${p.completion || 0}%` }} />
                      </div>
                      <span className="tabular-nums font-bold text-slate-800 w-8 text-right">{p.completion || 0}%</span>
                    </div>
                  </td>
                )}
                <td className="px-3 py-2.5 text-[11px] text-slate-500">
                  <div className="tabular-nums">{p.last_updated_at ? new Date(p.last_updated_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</div>
                  <div className="text-[10px] text-slate-400">by {p.last_updated_by_name || '—'}</div>
                </td>
                <td className="px-2 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <button className="p-1 rounded hover:bg-blue-100" title="Open file" onClick={(e) => { e.stopPropagation(); onOpen(p.id); }} data-testid={`pack-open-${p.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 text-blue-500" />
                    </button>
                    <button className="p-1 rounded hover:bg-rose-100" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} data-testid={`pack-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </button>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// SLA countdown badge with progress meter
function SlaCountdown({ days, indicator, sla }) {
  const tone = indicator === 'overdue' ? 'bg-rose-100 text-rose-800 border-rose-200'
    : indicator === 'due_today' ? 'bg-amber-100 text-amber-800 border-amber-200'
    : days !== null && days !== undefined && days <= 2 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-emerald-50 text-emerald-800 border-emerald-200';
  const total = Math.max(sla || 5, 1);
  const used = Math.min(1, Math.max(0, (total - days) / total));
  return (
    <div className="inline-flex flex-col items-end gap-0.5">
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black tabular-nums border ${tone}`}>
        {days < 0 ? `${Math.abs(days)}d OVR` : days === 0 ? 'DUE' : `${days}d`}
      </span>
      <div className="w-14 h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${indicator === 'overdue' ? 'bg-rose-500' : indicator === 'due_today' ? 'bg-amber-500' : days !== null && days <= 2 ? 'bg-amber-400' : 'bg-emerald-500'}`}
          style={{ width: `${Math.min(100, used * 100)}%` }} />
      </div>
    </div>
  );
}

// ── Lender Packs Settings — Demo Mode toggle ──
function LenderPacksSettings({ backendUrl }) {
  const [demoOn, setDemoOn] = useState(() => localStorage.getItem('mcc-demo-mode') === 'on');
  const [busy, setBusy] = useState(false);
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token') || ''}` });

  const toggleDemo = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!demoOn) {
        // Seed lender-packs demo (idempotent) so the demo account is populated.
        const r = await fetch(`${backendUrl}/api/lender-packs/demo/seed`, { method: 'POST', headers: authHeader() });
        if (!r.ok) throw new Error(await r.text());
        // Also seed marketing demo so both tools stay in sync.
        await fetch(`${backendUrl}/api/marketing/demo/seed`, { method: 'POST', headers: authHeader() }).catch(() => {});
        localStorage.setItem('mcc-demo-mode', 'on');
        toast.success('Demo Mode ON — you are now in the demo account.');
      } else {
        localStorage.setItem('mcc-demo-mode', 'off');
        toast.success('Demo Mode OFF — returned to your live account.');
      }
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.message || 'Failed to toggle Demo Mode');
      setBusy(false);
    }
  };

  const wipeDemo = async () => {
    if (!window.confirm('Delete every demo record from this workspace? Real data is untouched.')) return;
    setBusy(true);
    try {
      await fetch(`${backendUrl}/api/lender-packs/demo/reset`, { method: 'POST', headers: authHeader() });
      await fetch(`${backendUrl}/api/marketing/demo/reset`, { method: 'POST', headers: authHeader() }).catch(() => {});
      localStorage.removeItem('mcc-demo-mode');
      toast.success('Demo data wiped.');
      setTimeout(() => window.location.reload(), 400);
    } catch (e) { toast.error(e.message || 'Reset failed'); setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-indigo-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Demo Mode
            </p>
            <p className="text-base font-bold text-slate-900 mt-0.5">
              {demoOn ? 'You are inside the Demo Account' : 'You are on your Live Account'}
            </p>
            <p className="text-sm text-slate-600 mt-1 max-w-lg">
              Turn Demo Mode on to preview both My Derwent Hub and the Marketing Command Centre with a lived-in dummy dataset. Flip it off to return to your live account with your real work exactly as you left it.
            </p>
          </div>
          <label
            className="inline-flex items-center gap-3 select-none flex-shrink-0 cursor-pointer"
            data-testid="lp-demo-toggle"
          >
            <span className={`text-xs font-bold uppercase tracking-wider ${demoOn ? 'text-emerald-700' : 'text-slate-500'}`}>
              {busy ? 'Working…' : demoOn ? 'On' : 'Off'}
            </span>
            <button
              type="button"
              onClick={toggleDemo}
              disabled={busy}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-60 ${
                demoOn ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
              role="switch"
              aria-checked={demoOn}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  demoOn ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
              {busy && (
                <Loader2 className="w-3 h-3 animate-spin absolute left-1/2 -translate-x-1/2 text-white" />
              )}
            </button>
          </label>
        </div>
        {demoOn && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={wipeDemo} disabled={busy}
              className="text-rose-700 border-rose-200 hover:bg-rose-50">
              Wipe demo dataset entirely
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

